import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, Feather, Heart, Share2, LogOut, LogIn, User, UserCircle2, Menu, Library, GraduationCap, Info,
} from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import BrandLogo from '@/components/BrandLogo';
import Seo from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { useAuth } from '@/context/AuthContext';
import SearchFilters from '@/components/SearchFilters';
import HeroSearchTypeahead from '@/components/HeroSearchTypeahead';
import HomeStoriesSection from '@/components/HomeStoriesSection';
import { useTagOptions } from '@/hooks/useTagOptions';
import { useHeroTypeahead } from '@/hooks/useHeroTypeahead';
import {
  emptySelection,
  applySelectionToParams,
  totalSelectedCount,
} from '@/lib/filters';
import { SEARCH_SUGGESTIONS, SEARCH_PLACEHOLDER } from '@/lib/searchSuggestions';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, logout, isModerator } = useAuth();
  const { tagOptions } = useTagOptions();
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(() => emptySelection());

  // "Try:" suggestion chips and form submit both represent explicit
  // search intent — they include fresh=1 so SearchResultsPage routes
  // through the edge function (semantic + FTS + filters). The hero
  // typeahead chip pick path (handleToggleTag below) deliberately
  // omits fresh — that's a reactive narrowing, not a new search.
  const goToSearch = (q) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    const params = new URLSearchParams();
    params.set('q', trimmed);
    params.set('fresh', '1');
    const next = applySelectionToParams(params, selected);
    navigate(`/search?${next.toString()}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (!trimmed && totalSelectedCount(selected) === 0) return;
    closeNow();
    const params = new URLSearchParams();
    if (trimmed) params.set('q', trimmed);
    params.set('fresh', '1');
    const next = applySelectionToParams(params, selected);
    const qs = next.toString();
    navigate(qs ? `/search?${qs}` : '/search');
  };

  // LandingPage is a launchpad — adding ANY filter (via typeahead or the
  // filter sheet) means "show me results now," so we navigate straight
  // to /search. Removing a chip stays put (the user is cleaning up
  // before committing). Once at /search the URL is the source of truth
  // and the page re-searches on every change automatically — there's
  // no need to force the filter sheet open on top of the results.
  const handleToggleTag = useCallback(
    (dimensionKey, tagId) => {
      const wasSelected = selected[dimensionKey]?.has(tagId);
      const nextSet = new Set(selected[dimensionKey] || []);
      if (wasSelected) nextSet.delete(tagId);
      else nextSet.add(tagId);
      const next = { ...selected, [dimensionKey]: nextSet };
      setSelected(next);

      if (!wasSelected) {
        const params = new URLSearchParams();
        const trimmed = searchQuery.trim();
        if (trimmed) params.set('q', trimmed);
        const withFilters = applySelectionToParams(params, next);
        navigate(`/search?${withFilters.toString()}`);
      }
    },
    [navigate, searchQuery, selected]
  );

  const handleClearFilters = () => {
    setSelected(emptySelection());
  };

  const { inputRef, isOpen, inputProps, onTypeaheadPick, closeNow } =
    useHeroTypeahead({ onPick: handleToggleTag });

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Katha Mithra · कथा मित्र — Stories of inner transformation"
        description="A friend of stories. Real personal-transformation stories rooted in spiritual practice — search them by feeling, by life area, or by the shift they brought."
        path="/"
      />
      <nav className="bg-[#FDFBF7]/80 backdrop-blur-md border-b border-[#E6E2D6] sticky top-0 z-50" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-between items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 sm:space-x-3 shrink-0 hover:opacity-80 transition-opacity"
              aria-label="Go to home"
            >
              <BrandLogo className="h-8 w-8 sm:h-10 sm:w-10" />
              <div>
                <h1 className="text-lg sm:text-2xl font-serif font-semibold text-[#2D2A26] whitespace-nowrap">Katha Mithra</h1>
                <p className="text-[10px] sm:text-xs text-[#6B665E]">कथा मित्र</p>
              </div>
            </button>
            {/* Desktop nav (≥ md) — buttons inline */}
            <div className="hidden md:flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/browse')}
                className="text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                data-testid="browse-stories-nav-btn"
              >
                <BrandLogo className="h-4 w-4 mr-2" />
                Browse Stories
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/submit')}
                className="text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                data-testid="share-story-nav-btn"
              >
                <Feather className="h-4 w-4 mr-2" />
                Share Your Story
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/learn')}
                className="text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                data-testid="learn-nav-btn"
              >
                <GraduationCap className="h-4 w-4 mr-2" />
                Learn
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/about')}
                className="text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                data-testid="about-nav-btn"
              >
                <Info className="h-4 w-4 mr-2" />
                About
              </Button>
              {user && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/my-stories')}
                  className="text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                  data-testid="my-stories-nav-btn"
                >
                  <Library className="h-4 w-4 mr-2" />
                  My Stories
                </Button>
              )}
              {isModerator && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/moderation')}
                  className="text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                  data-testid="moderation-nav-btn"
                >
                  Moderation
                </Button>
              )}
              {user ? (
                <div className="flex items-center space-x-1">
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/profile')}
                    className="text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6] gap-2"
                    data-testid="profile-nav-btn"
                  >
                    <Avatar className="h-6 w-6 border border-[#E6E2D6]">
                      {user.avatar_url ? <AvatarImage src={user.avatar_url} alt="" /> : null}
                      <AvatarFallback className="bg-[#FBF4EB] text-[#C05621] text-[10px] font-medium">
                        {(user.name || '').split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?'}
                      </AvatarFallback>
                    </Avatar>
                    {user.name}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={logout}
                    className="text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                  data-testid="login-nav-btn"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Login
                </Button>
              )}
            </div>

            {/* Mobile nav (< md) — hamburger + slide-down sheet */}
            <div className="md:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Open menu"
                    className="text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                    data-testid="mobile-menu-btn"
                  >
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72 bg-[#FDFBF7] flex flex-col gap-2 pt-12">
                  {user && (
                    <div
                      className="flex items-center gap-2 mb-2 cursor-pointer hover:opacity-80"
                      onClick={() => navigate('/profile')}
                    >
                      <Avatar className="h-8 w-8 border border-[#E6E2D6]">
                        {user.avatar_url ? <AvatarImage src={user.avatar_url} alt="" /> : null}
                        <AvatarFallback className="bg-[#FBF4EB] text-[#C05621] text-xs font-medium">
                          {(user.name || '').split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium text-[#2D2A26]">{user.name}</p>
                        <p className="text-xs text-[#6B665E]">View profile</p>
                      </div>
                    </div>
                  )}
                  <SheetClose asChild>
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/browse')}
                      className="justify-start text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                    >
                      <BrandLogo className="h-4 w-4 mr-2" />
                      Browse Stories
                    </Button>
                  </SheetClose>
                  <SheetClose asChild>
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/submit')}
                      className="justify-start text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                    >
                      <Feather className="h-4 w-4 mr-2" />
                      Share Your Story
                    </Button>
                  </SheetClose>
                  <SheetClose asChild>
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/learn')}
                      className="justify-start text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                    >
                      <GraduationCap className="h-4 w-4 mr-2" />
                      Learn
                    </Button>
                  </SheetClose>
                  <SheetClose asChild>
                    <Button
                      variant="ghost"
                      onClick={() => navigate('/about')}
                      className="justify-start text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                      data-testid="about-mobile-nav-btn"
                    >
                      <Info className="h-4 w-4 mr-2" />
                      About
                    </Button>
                  </SheetClose>
                  {user && (
                    <SheetClose asChild>
                      <Button
                        variant="ghost"
                        onClick={() => navigate('/my-stories')}
                        className="justify-start text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                        data-testid="my-stories-mobile-nav-btn"
                      >
                        <Library className="h-4 w-4 mr-2" />
                        My Stories
                      </Button>
                    </SheetClose>
                  )}
                  {user && (
                    <SheetClose asChild>
                      <Button
                        variant="ghost"
                        onClick={() => navigate('/profile')}
                        className="justify-start text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                        data-testid="profile-mobile-nav-btn"
                      >
                        <UserCircle2 className="h-4 w-4 mr-2" />
                        Profile
                      </Button>
                    </SheetClose>
                  )}
                  {isModerator && (
                    <SheetClose asChild>
                      <Button
                        variant="ghost"
                        onClick={() => navigate('/moderation')}
                        className="justify-start text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                      >
                        Moderation
                      </Button>
                    </SheetClose>
                  )}
                  {user ? (
                    <SheetClose asChild>
                      <Button
                        variant="ghost"
                        onClick={logout}
                        className="justify-start text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Logout
                      </Button>
                    </SheetClose>
                  ) : (
                    <SheetClose asChild>
                      <Button
                        variant="ghost"
                        onClick={() => navigate('/login')}
                        className="justify-start text-[#2D2A26] hover:text-[#C05621] hover:bg-[#F5EFE6]"
                      >
                        <LogIn className="h-4 w-4 mr-2" />
                        Login
                      </Button>
                    </SheetClose>
                  )}
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>

      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl md:text-7xl font-serif tracking-tight leading-[1.1] text-[#2D2A26] mb-5 px-1">
            Your friend in transformation
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-[#6B665E] mb-8">
            A searchable library of stories that elevate, ground, and awaken. Search by what you are going through, not just by topic, and find a story that meets you there.
          </p>

          <div className="max-w-3xl mx-auto w-full text-left">
            <SearchFilters
              selected={selected}
              tagOptions={tagOptions}
              onToggleTag={handleToggleTag}
              onClearAll={handleClearFilters}
              compact
              align="left"
              searchForm={
                <>
                  <form onSubmit={handleSearch} className="w-full relative" data-testid="hero-search-form">
                    <div className="bg-white rounded-2xl sm:rounded-full shadow-lg border border-[#E6E2D6] p-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-0 sm:space-x-2">
                      <div className="flex flex-1 min-w-0 items-center gap-2 pl-1 sm:pl-0">
                        <Search className="h-5 w-5 text-[#6B665E] ml-2 sm:ml-4 shrink-0" aria-hidden />
                        <Input
                          ref={inputRef}
                          type="text"
                          placeholder={SEARCH_PLACEHOLDER}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="flex-1 border-0 focus:ring-0 bg-transparent text-[#2D2A26] placeholder:text-[#6B665E] min-w-0"
                          data-testid="hero-search-input"
                          autoComplete="off"
                          {...inputProps}
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-4 sm:py-5 md:py-6 text-base sm:text-lg font-medium bg-[#C05621] hover:bg-[#A0461A] text-white shadow-lg hover:shadow-xl btn-primary shrink-0"
                        data-testid="hero-search-btn"
                      >
                        Search
                      </Button>
                    </div>
                    <HeroSearchTypeahead
                      query={searchQuery}
                      tagOptions={tagOptions}
                      selected={selected}
                      onToggleTag={onTypeaheadPick}
                      open={isOpen}
                    />
                  </form>
                  <div
                    className="flex flex-wrap items-center gap-2 pt-2"
                    data-testid="hero-suggestions"
                  >
                    <span className="text-sm text-[#6B665E] italic shrink-0">Try:</span>
                    {SEARCH_SUGGESTIONS.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => goToSearch(q)}
                        className="px-3.5 py-1.5 rounded-full bg-white border border-[#E6E2D6] text-[#2D2A26] hover:border-[#C05621] hover:bg-[#F5EFE6] hover:text-[#C05621] text-sm transition-colors"
                        data-testid={`hero-suggestion-${q.replace(/\s+/g, '-')}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </>
              }
            />
          </div>
        </div>
      </section>

      {/* ----- Stories ------------------------------------------------ */}
      {/* Sits directly under the hero, before any of the explanatory
          sections. On a phone the whole page below the fold is the site
          talking about itself; without this, a first visit can end with no
          evidence that a story exists. Self-removes when empty or failing —
          see HomeStoriesSection. */}
      <HomeStoriesSection tagOptions={tagOptions} />

      {/* ----- Philosophy --------------------------------------------- */}
      {/* The strongest three ideas from the About page, compressed for
          mobile visitors who will never click through. Quiet typography
          (no card chrome, narrow column, italic on the link) keeps this
          a pause between the hero and the action sections, not a
          competing pitch. */}
      <section className="py-8 md:py-10" data-testid="philosophy-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl sm:text-3xl md:text-4xl font-serif tracking-tight leading-snug text-[#2D2A26] mb-10">
            What we consume shapes who we become.
          </p>
          <p className="text-base md:text-lg leading-relaxed text-[#6B665E] mb-5">
            Most platforms are built to keep us scrolling. Katha Mithra is built to help us pause, listen, reflect, and rise.
          </p>
          <p className="text-base md:text-lg leading-relaxed text-[#6B665E] mb-8">
            No infinite scroll. No trending noise. Just stories held in an environment designed for clarity.
          </p>
          <button
            type="button"
            onClick={() => navigate('/about')}
            className="text-[#C05621] hover:text-[#A0461A] font-medium hover:underline underline-offset-4"
            data-testid="philosophy-read-more-btn"
          >
            Read the philosophy →
          </button>
        </div>
      </section>

      <section className="py-20 bg-[#F5EFE6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight leading-tight text-center text-[#2D2A26] mb-16 px-2">
            How Katha Mithra Works
          </h3>
          {/* The three-card arc — meet you, mirror, light the way —
              mirrors the thematic spine of the About page. Each body
              echoes a load-bearing line: "stories that meet you where
              you are" → "inspiration becomes a mirror" → "let it light
              the way for someone else." Edit these together, not
              separately, so the voice stays one. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-[#E6E2D6]" data-testid="feature-search">
              <div className="w-16 h-16 rounded-full bg-[#C05621] flex items-center justify-center mb-6">
                <Search className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-2xl md:text-3xl font-serif font-medium text-[#2D2A26] mb-4">Search by inner state</h4>
              <p className="text-base md:text-lg leading-relaxed text-[#6B665E]">
                Type what you feel, not just what you want. Katha Mithra finds stories that meet you where you are.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-[#E6E2D6]" data-testid="feature-discover">
              <div className="w-16 h-16 rounded-full bg-[#4A5D4F] flex items-center justify-center mb-6">
                <Heart className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-2xl md:text-3xl font-serif font-medium text-[#2D2A26] mb-4">Read as a mirror</h4>
              <p className="text-base md:text-lg leading-relaxed text-[#6B665E]">
                Every story carries inner dimensions: what was faced, what helped, and what shifted. You don&rsquo;t just read a story. You see the movement within it.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-[#E6E2D6]" data-testid="feature-share">
              <div className="w-16 h-16 rounded-full bg-[#C05621] flex items-center justify-center mb-6">
                <Share2 className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-2xl md:text-3xl font-serif font-medium text-[#2D2A26] mb-4">Share what moved you</h4>
              <p className="text-base md:text-lg leading-relaxed text-[#6B665E]">
                When a story moves you, share it as a formatted card to
                Instagram, LinkedIn, or YouTube. Let it light the way for
                someone else.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ----- What lives here ---------------------------------------- */}
      {/* Compact preview of the four story types from About section 6.
          One live, three unfolding. Chips are kept lightweight (single
          line + status) on purpose: the homepage signals scope without
          re-explaining what the About page already explains in depth. */}
      <section className="py-20" data-testid="story-types-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight leading-tight text-center text-[#2D2A26] mb-4 px-2">
            What lives here
          </h3>
          <p className="text-base md:text-lg leading-relaxed text-[#6B665E] text-center mb-12 max-w-2xl mx-auto">
            Personal Transformation Stories are live now. More kinds of elevating stories will unfold over time.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-[#C05621]/40 rounded-xl p-5 relative" data-testid="story-type-personal">
              <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.18em] text-[#C05621] bg-[#FBF4EB] border border-[#C05621]/30 rounded-full px-2 py-0.5">Live</span>
              <h4 className="text-lg font-serif font-medium text-[#2D2A26] mb-2 pr-12">Personal Transformation</h4>
              <p className="text-sm text-[#6B665E] leading-relaxed">Real people. Real struggles. A way through.</p>
            </div>
            <div className="bg-white border border-[#E6E2D6] rounded-xl p-5" data-testid="story-type-sustainable">
              <h4 className="text-lg font-serif font-medium text-[#2D2A26] mb-2">Sustainable Living</h4>
              <p className="text-sm text-[#6B665E] leading-relaxed">People quietly healing the world.</p>
            </div>
            <div className="bg-white border border-[#E6E2D6] rounded-xl p-5" data-testid="story-type-wisdom">
              <h4 className="text-lg font-serif font-medium text-[#2D2A26] mb-2">Wisdom Stories</h4>
              <p className="text-sm text-[#6B665E] leading-relaxed">What our ancestors saw clearly.</p>
            </div>
            <div className="bg-white border border-[#E6E2D6] rounded-xl p-5" data-testid="story-type-kala">
              <h4 className="text-lg font-serif font-medium text-[#2D2A26] mb-2">Kala &amp; Mastery</h4>
              <p className="text-sm text-[#6B665E] leading-relaxed">Mastery as a path of becoming.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ----- CTA ---------------------------------------------------- */}
      {/* Warmer framing than the old "Your journey can inspire someone
          else." The "lamp for someone walking a similar path" image
          echoes a deepa/dīpa-stambha sensibility and shows up identically
          on the About page CTA, so both pages end on the same emotional
          note. */}
      <section className="py-20 bg-[#F5EFE6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight leading-tight text-[#2D2A26] mb-6 px-2">
            Has a story shaped you?
          </h3>
          <p className="text-base md:text-lg leading-relaxed text-[#6B665E] mb-10 max-w-2xl mx-auto">
            Share your transformation story so it may become a lamp for someone walking a similar path.
          </p>
          <Button
            onClick={() => navigate('/submit')}
            className="rounded-full px-8 py-6 text-lg font-medium bg-[#C05621] hover:bg-[#A0461A] text-white shadow-lg hover:shadow-xl btn-primary"
            data-testid="cta-share-story-btn"
          >
            <Feather className="h-5 w-5 mr-2" />
            Share Your Story
          </Button>
        </div>
      </section>

      <footer className="bg-[#2D2A26] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <BrandLogo className="h-8 w-8" />
            <span className="text-xl font-serif">Katha Mithra • कथा मित्र</span>
          </div>
          <p className="text-sm text-gray-400">
            Your companion in discovering transformative experiences
          </p>
          {/* A real <a href>, not a router button: this is the one link that
              tells a search engine these two sites belong to each other.
              moderndaykarmayogi.com already links here from its header and
              its Katha Mithra section; until now nothing pointed back. */}
          <p className="mt-3 text-sm text-gray-400">
            A{' '}
            <a
              href="https://www.moderndaykarmayogi.com/"
              className="text-gray-300 hover:text-white underline underline-offset-4"
              data-testid="footer-mdky-link"
            >
              ModernDayKarmaYogi
            </a>{' '}
            initiative
          </p>
          <div className="mt-4 flex items-center justify-center gap-5 text-sm">
            {/* Browse is otherwise reachable only through the mobile
                hamburger — the footer is the second exit for anyone who
                scrolled the whole page without searching. These are Links,
                not buttons: Link renders a real <a href> and still navigates
                client-side, so a crawler can follow them. A button is a dead
                end to anything that is not a mouse. */}
            <Link
              to="/browse"
              className="text-gray-300 hover:text-white hover:underline underline-offset-4"
              data-testid="footer-browse-link"
            >
              Browse Stories
            </Link>
            <span className="text-gray-600" aria-hidden>·</span>
            <Link
              to="/about"
              className="text-gray-300 hover:text-white hover:underline underline-offset-4"
              data-testid="footer-about-link"
            >
              About
            </Link>
            <span className="text-gray-600" aria-hidden>·</span>
            <Link
              to="/learn"
              className="text-gray-300 hover:text-white hover:underline underline-offset-4"
              data-testid="footer-learn-link"
            >
              Learn
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;